import * as React from "react"
import { motion } from "motion/react"
import { Send, Globe, Building2, Loader2, Upload, Link, X, Plus, Image as ImageIcon, Film } from "lucide-react"
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { useAuth, type AdminChatActivityData, type AdminAgencyTarget } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"
import { CHART_COLORS, AXIS_STYLE, GRID_STYLE, TOOLTIP_STYLE } from "./AdminChartTheme"

export function AdminChat() {
  const {
    user,
    getAdminChatActivity,
    getAdminAgencyTargets,
    postAdminSystemChatMessage,
    getPresignedUrl,
    getAdminChatControl,
    setAdminChatControl,
    createAdminAmaEvent,
  } = useAuth()
  const [chatData, setChatData] = React.useState<AdminChatActivityData | null>(null)
  const [loading, setLoading] = React.useState(true)

  const [chatControl, setChatControl] = React.useState<{
    systemOnlyMode: boolean
    systemOnlyUntil: string | null
    reason: string | null
  } | null>(null)
  const [lockDurationMinutes, setLockDurationMinutes] = React.useState(60)
  const [lockReason, setLockReason] = React.useState("")
  const [isUpdatingControl, setIsUpdatingControl] = React.useState(false)

  // AMA / webinar state
  const [amaTitle, setAmaTitle] = React.useState("")
  const [amaDescription, setAmaDescription] = React.useState("")
  const [amaHostName, setAmaHostName] = React.useState("Oval Team")
  const [amaStartsAtLocal, setAmaStartsAtLocal] = React.useState("")
  const [amaDurationMinutes, setAmaDurationMinutes] = React.useState(60)
  const [amaJoinUrl, setAmaJoinUrl] = React.useState("")
  const [amaStreamUrl, setAmaStreamUrl] = React.useState("")
  const [amaTargetMode, setAmaTargetMode] = React.useState<"all" | "specific">("all")
  const [amaTargetAgencyId, setAmaTargetAgencyId] = React.useState("")
  const [isCreatingAma, setIsCreatingAma] = React.useState(false)
  const [amaFeedback, setAmaFeedback] = React.useState<{ type: "success" | "error"; text: string } | null>(null)

  // System message state
  const [agencyTargets, setAgencyTargets] = React.useState<AdminAgencyTarget[]>([])
  const [targetMode, setTargetMode] = React.useState<"all" | "specific">("all")
  const [selectedAgencyId, setSelectedAgencyId] = React.useState("")
  const [systemMessage, setSystemMessage] = React.useState("")
  const [isPosting, setIsPosting] = React.useState(false)
  const [feedback, setFeedback] = React.useState<{ type: "success" | "error"; text: string } | null>(null)

  // Media state
  const [mediaMode, setMediaMode] = React.useState<"none" | "upload" | "url">("none")
  const [mediaUrl, setMediaUrl] = React.useState("")
  const [mediaFileType, setMediaFileType] = React.useState("")
  const [mediaFileName, setMediaFileName] = React.useState("")
  const [isUploading, setIsUploading] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // CTA state
  const [ctas, setCtas] = React.useState<Array<{ label: string; url: string }>>([])
  const isSuperAdmin = user?.role === "super_admin"

  const addCta = () => {
    if (ctas.length >= 3) return
    setCtas([...ctas, { label: "", url: "" }])
  }

  const removeCta = (index: number) => {
    setCtas(ctas.filter((_, i) => i !== index))
  }

  const updateCta = (index: number, field: "label" | "url", value: string) => {
    setCtas(ctas.map((c, i) => (i === index ? { ...c, [field]: value } : c)))
  }

  const removeMedia = () => {
    setMediaMode("none")
    setMediaUrl("")
    setMediaFileType("")
    setMediaFileName("")
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const result = await getPresignedUrl(file.name, file.type)
      if (!result.success || !result.uploadUrl || !result.fileUrl) {
        setFeedback({ type: "error", text: result.message || "Failed to get upload URL" })
        setIsUploading(false)
        return
      }

      await fetch(result.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      })

      setMediaUrl(result.fileUrl)
      setMediaFileType(file.type)
      setMediaFileName(file.name)
    } catch {
      setFeedback({ type: "error", text: "Upload failed" })
    }
    setIsUploading(false)
  }

  const detectMediaType = (url: string): string => {
    const lower = url.toLowerCase()
    if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(lower)) return "image"
    if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/.test(lower)) return "video"
    return "image" // default to image
  }

  React.useEffect(() => {
    const load = async () => {
      setLoading(true)
      const [chatRes, agencyRes, controlRes] = await Promise.all([
        getAdminChatActivity(),
        getAdminAgencyTargets(),
        getAdminChatControl(),
      ])
      if (chatRes.success && chatRes.data) setChatData(chatRes.data)
      if (agencyRes.success && agencyRes.agencies) setAgencyTargets(agencyRes.agencies)
      if (controlRes.success && controlRes.control) {
        setChatControl(controlRes.control)
        setLockReason(controlRes.control.reason || "")
      }
      setLoading(false)
    }
    load()
  }, [getAdminChatActivity, getAdminAgencyTargets, getAdminChatControl])

  const refreshChatControl = React.useCallback(async () => {
    const res = await getAdminChatControl()
    if (res.success && res.control) {
      setChatControl(res.control)
      setLockReason(res.control.reason || "")
    }
  }, [getAdminChatControl])

  const handleEnableSystemOnlyMode = async () => {
    if (!isSuperAdmin) {
      setFeedback({ type: "error", text: "Only super admins can enable system-only mode." })
      return
    }
    setIsUpdatingControl(true)
    setFeedback(null)
    const result = await setAdminChatControl({
      systemOnlyMode: true,
      durationMinutes: lockDurationMinutes,
      reason: lockReason.trim() || undefined,
    })
    if (result.success && result.control) {
      setChatControl(result.control)
      setFeedback({ type: "success", text: result.message || "System-only mode enabled." })
    } else {
      setFeedback({ type: "error", text: result.message || "Failed to update chat mode." })
    }
    setIsUpdatingControl(false)
  }

  const handleDisableSystemOnlyMode = async () => {
    if (!isSuperAdmin) {
      setFeedback({ type: "error", text: "Only super admins can disable system-only mode." })
      return
    }
    setIsUpdatingControl(true)
    setFeedback(null)
    const result = await setAdminChatControl({ systemOnlyMode: false })
    if (result.success && result.control) {
      setChatControl(result.control)
      setFeedback({ type: "success", text: result.message || "System-only mode disabled." })
    } else {
      setFeedback({ type: "error", text: result.message || "Failed to update chat mode." })
    }
    setIsUpdatingControl(false)
  }

  const canCreateAma =
    !isCreatingAma &&
    amaTitle.trim().length > 0 &&
    amaHostName.trim().length > 0 &&
    amaStartsAtLocal.trim().length > 0 &&
    amaJoinUrl.trim().length > 0 &&
    (amaTargetMode === "all" || amaTargetAgencyId.length > 0)

  const handleCreateAmaEvent = async () => {
    if (!canCreateAma) return

    const startsAtDate = new Date(amaStartsAtLocal)
    if (Number.isNaN(startsAtDate.getTime())) {
      setAmaFeedback({ type: "error", text: "Please choose a valid date and time." })
      return
    }

    setIsCreatingAma(true)
    setAmaFeedback(null)
    const result = await createAdminAmaEvent({
      title: amaTitle.trim(),
      description: amaDescription.trim() || undefined,
      hostName: amaHostName.trim(),
      startsAt: startsAtDate.toISOString(),
      durationMinutes: amaDurationMinutes,
      joinUrl: amaJoinUrl.trim(),
      streamUrl: amaStreamUrl.trim() || undefined,
      sendToAll: amaTargetMode === "all",
      agencyId: amaTargetMode === "specific" ? amaTargetAgencyId : undefined,
    })

    if (result.success) {
      setAmaFeedback({
        type: "success",
        text: result.message || "AMA created and posted to chat.",
      })
      setAmaTitle("")
      setAmaDescription("")
      setAmaStartsAtLocal("")
      setAmaJoinUrl("")
      setAmaStreamUrl("")
      if (amaTargetMode === "specific") {
        setAmaTargetAgencyId("")
      }
    } else {
      setAmaFeedback({ type: "error", text: result.message || "Failed to create AMA event." })
    }
    setIsCreatingAma(false)
  }

  const validCtas = ctas.filter((c) => c.label.trim() && c.url.trim())
  const canSend =
    !isPosting &&
    !isUploading &&
    systemMessage.trim().length > 0 &&
    (targetMode === "all" || selectedAgencyId.length > 0)

  const handleSend = async () => {
    if (!canSend) return
    setIsPosting(true)
    setFeedback(null)

    const payload: Parameters<typeof postAdminSystemChatMessage>[0] = {
      content: systemMessage.trim(),
      sendToAll: targetMode === "all",
      agencyId: targetMode === "specific" ? selectedAgencyId : undefined,
    }

    if (mediaUrl) {
      payload.fileUrl = mediaUrl
      payload.fileType = mediaFileType || (detectMediaType(mediaUrl) === "video" ? "video/mp4" : "image/png")
      if (mediaFileName) payload.fileName = mediaFileName
    }

    if (validCtas.length > 0) {
      payload.ctas = validCtas
    }

    const result = await postAdminSystemChatMessage(payload)

    if (result.success) {
      setSystemMessage("")
      removeMedia()
      setCtas([])
      setFeedback({ type: "success", text: result.message || "System message posted." })
    } else {
      setFeedback({ type: "error", text: result.message || "Failed to post." })
    }
    setIsPosting(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
      </div>
    )
  }

  const byAgencyChart = (chatData?.byAgency || []).slice(0, 10).map((a) => ({
    name: a.agencyName.length > 14 ? a.agencyName.slice(0, 14) + "..." : a.agencyName,
    messages: a.messageCount,
  }))

  const dailyChart = (chatData?.dailyVolume || []).map((d) => ({
    ...d,
    label: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }))

  const lockUntilDate = chatControl?.systemOnlyUntil ? new Date(chatControl.systemOnlyUntil) : null
  const lockUntilLabel =
    lockUntilDate && !Number.isNaN(lockUntilDate.getTime())
      ? lockUntilDate.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : null

  return (
    <div className="space-y-6">
      <AdminCard delay={0}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-white/80 font-admin">Temporary Chat Lock</h3>
            <p className="text-xs text-white/35 mt-1 font-admin">
              When enabled, user chat is blocked and only super-admin system messages are allowed.
            </p>
            {!isSuperAdmin && (
              <p className="text-xs text-amber-300/90 mt-1 font-admin">
                View-only for support admins. Ask a super admin to change lock state.
              </p>
            )}
            <div className="mt-2 text-xs font-admin">
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 border ${
                  chatControl?.systemOnlyMode
                    ? "text-amber-300 border-amber-300/30 bg-amber-300/10"
                    : "text-emerald-300 border-emerald-300/30 bg-emerald-300/10"
                }`}
              >
                {chatControl?.systemOnlyMode ? "System-only mode active" : "Chat open"}
              </span>
              {chatControl?.systemOnlyMode && lockUntilLabel && (
                <span className="ml-2 text-white/50">Until {lockUntilLabel}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refreshChatControl}
              className="px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 hover:text-white transition-colors font-admin"
              disabled={isUpdatingControl}
            >
              Refresh
            </button>
            {chatControl?.systemOnlyMode ? (
              <button
                onClick={handleDisableSystemOnlyMode}
                disabled={isUpdatingControl || !isSuperAdmin}
                className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-xs text-red-300 hover:bg-red-500/30 transition-colors disabled:opacity-60 font-admin"
              >
                {isUpdatingControl ? "Updating..." : "Disable Now"}
              </button>
            ) : (
              <button
                onClick={handleEnableSystemOnlyMode}
                disabled={isUpdatingControl || !isSuperAdmin}
                className="px-3 py-2 rounded-lg bg-[#F37020]/20 border border-[#F37020]/30 text-xs text-[#F37020] hover:bg-[#F37020]/30 transition-colors disabled:opacity-60 font-admin"
              >
                {isUpdatingControl ? "Updating..." : "Enable System-Only"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3">
          <label className="text-xs text-white/40 font-admin space-y-1">
            Lock Duration
            <select
              value={lockDurationMinutes}
              onChange={(e) => setLockDurationMinutes(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin outline-none focus:border-[#F37020]/40 transition-colors"
              disabled={isUpdatingControl || !isSuperAdmin}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={360}>6 hours</option>
              <option value={720}>12 hours</option>
              <option value={1440}>24 hours</option>
            </select>
          </label>

          <label className="text-xs text-white/40 font-admin space-y-1">
            Reason (optional)
            <input
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              maxLength={300}
              placeholder="Maintenance, moderation event, etc."
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              disabled={isUpdatingControl || !isSuperAdmin}
            />
          </label>
        </div>
      </AdminCard>

      <AdminCard delay={0.03}>
        <h3 className="text-sm font-semibold text-white/80 font-admin">Create AMA / Webinar Event</h3>
        <p className="text-xs text-white/35 mt-1 font-admin">
          Publish a scheduled AMA and automatically announce it in chat with a join button.
        </p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-white/40 font-admin space-y-1">
            Event Title
            <input
              value={amaTitle}
              onChange={(e) => setAmaTitle(e.target.value)}
              maxLength={140}
              placeholder="Top Producer AMA"
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
            />
          </label>

          <label className="text-xs text-white/40 font-admin space-y-1">
            Host Name
            <input
              value={amaHostName}
              onChange={(e) => setAmaHostName(e.target.value)}
              maxLength={120}
              placeholder="Agency Growth Team"
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
            />
          </label>

          <label className="text-xs text-white/40 font-admin space-y-1">
            Start Date & Time
            <input
              type="datetime-local"
              value={amaStartsAtLocal}
              onChange={(e) => setAmaStartsAtLocal(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin outline-none focus:border-[#F37020]/40 transition-colors"
            />
          </label>

          <label className="text-xs text-white/40 font-admin space-y-1">
            Duration
            <select
              value={amaDurationMinutes}
              onChange={(e) => setAmaDurationMinutes(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin outline-none focus:border-[#F37020]/40 transition-colors"
            >
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
              <option value={120}>120 minutes</option>
            </select>
          </label>
        </div>

        <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
          Join URL
          <input
            value={amaJoinUrl}
            onChange={(e) => setAmaJoinUrl(e.target.value)}
            maxLength={800}
            placeholder="https://zoom.us/..."
            className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
          />
        </label>

        <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
          Stream Embed URL (optional, YouTube/Twitch)
          <input
            value={amaStreamUrl}
            onChange={(e) => setAmaStreamUrl(e.target.value)}
            maxLength={800}
            placeholder="https://youtube.com/watch?v=... or https://twitch.tv/..."
            className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
          />
        </label>

        <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
          Event Description (optional)
          <textarea
            value={amaDescription}
            onChange={(e) => setAmaDescription(e.target.value)}
            rows={3}
            maxLength={1200}
            placeholder="What this session covers..."
            className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 resize-y transition-colors"
          />
        </label>

        <div className="mt-3 flex flex-wrap gap-3">
          <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
            <input
              type="radio"
              name="ama-target"
              checked={amaTargetMode === "all"}
              onChange={() => setAmaTargetMode("all")}
              className="accent-[#F37020]"
            />
            <Globe className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs font-medium text-white/60 font-admin">All Agencies</span>
          </label>
          <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
            <input
              type="radio"
              name="ama-target"
              checked={amaTargetMode === "specific"}
              onChange={() => setAmaTargetMode("specific")}
              className="accent-[#F37020]"
            />
            <Building2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-medium text-white/60 font-admin">Specific Agency</span>
          </label>
        </div>

        {amaTargetMode === "specific" && (
          <select
            value={amaTargetAgencyId}
            onChange={(e) => setAmaTargetAgencyId(e.target.value)}
            className="mt-3 w-full px-4 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin outline-none focus:border-[#F37020]/40 transition-colors"
          >
            <option value="">Select an agency</option>
            {agencyTargets.map((t) => (
              <option key={t.agencyId} value={t.agencyId}>
                {t.agencyName} ({t.agencyId})
              </option>
            ))}
          </select>
        )}

        {amaFeedback && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium ${
              amaFeedback.type === "success"
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : "bg-red-500/10 text-red-400 border border-red-500/20"
            }`}
          >
            {amaFeedback.text}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-white/20 font-mono-admin">{amaTitle.length}/140</span>
          <button
            onClick={handleCreateAmaEvent}
            disabled={!canCreateAma}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              canCreateAma
                ? "bg-[#F59E0B] text-black hover:bg-[#E88F07]"
                : "bg-white/[0.04] text-white/20 cursor-not-allowed"
            }`}
          >
            {isCreatingAma ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Create AMA
          </button>
        </div>
      </AdminCard>

      {/* Messages per Agency */}
      {byAgencyChart.length > 0 && (
        <AdminCard delay={0.05}>
          <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">Messages per Agency (30 days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byAgencyChart} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid {...GRID_STYLE} horizontal={false} />
                <XAxis type="number" {...AXIS_STYLE} tick={{ fill: "#52525B", fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  {...AXIS_STYLE}
                  tick={{ fill: "#71717A", fontSize: 11 }}
                  width={110}
                />
                <Tooltip {...TOOLTIP_STYLE} />
                <Bar dataKey="messages" fill={CHART_COLORS.cyan} radius={[0, 4, 4, 0]} barSize={16} name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </AdminCard>
      )}

      {/* Daily Message Volume */}
      {dailyChart.length > 0 && (
        <AdminCard delay={0.1}>
          <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">Daily Message Volume</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyChart} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.violet} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.violet} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID_STYLE} />
                <XAxis dataKey="label" {...AXIS_STYLE} tick={{ fill: "#52525B", fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis {...AXIS_STYLE} tick={{ fill: "#52525B", fontSize: 10 }} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="count" stroke={CHART_COLORS.violet} fill="url(#gradMessages)" strokeWidth={2} name="Messages" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </AdminCard>
      )}

      {/* System Message Broadcast */}
      <AdminCard delay={0.2}>
        <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">System Message Broadcast</h3>
        <p className="text-xs text-white/30 mb-3 font-admin">
          Post an official message to all agency chats or a specific agency.
        </p>

        <textarea
          value={systemMessage}
          onChange={(e) => setSystemMessage(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Write your system message..."
          className="w-full px-4 py-3 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 resize-y transition-colors"
        />

        {/* Media Section */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/40 font-admin">Attach Media</span>
            <div className="flex gap-1">
              <button
                onClick={() => { setMediaMode(mediaMode === "upload" ? "none" : "upload"); setMediaUrl(""); setMediaFileType(""); setMediaFileName("") }}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  mediaMode === "upload" ? "bg-[#F37020]/20 text-[#F37020] border border-[#F37020]/30" : "bg-[#0C0C12] text-white/40 border border-[#2A2A45] hover:border-white/10"
                }`}
              >
                <Upload className="w-3 h-3 inline mr-1" />Upload
              </button>
              <button
                onClick={() => { setMediaMode(mediaMode === "url" ? "none" : "url"); setMediaUrl(""); setMediaFileType(""); setMediaFileName("") }}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  mediaMode === "url" ? "bg-[#F37020]/20 text-[#F37020] border border-[#F37020]/30" : "bg-[#0C0C12] text-white/40 border border-[#2A2A45] hover:border-white/10"
                }`}
              >
                <Link className="w-3 h-3 inline mr-1" />URL
              </button>
            </div>
          </div>

          {mediaMode === "upload" && !mediaUrl && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full px-4 py-3 rounded-lg border border-dashed border-[#2A2A45] bg-[#0C0C12] text-white/30 text-xs font-admin hover:border-white/20 transition-colors"
              >
                {isUploading ? (
                  <span className="flex items-center justify-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Uploading...</span>
                ) : (
                  "Click to select image or video"
                )}
              </button>
            </div>
          )}

          {mediaMode === "url" && !mediaUrl && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Paste image or video URL..."
                className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = (e.target as HTMLInputElement).value.trim()
                    if (val) {
                      setMediaUrl(val)
                      const type = detectMediaType(val)
                      setMediaFileType(type === "video" ? "video/mp4" : "image/png")
                    }
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.querySelector<HTMLInputElement>('[placeholder="Paste image or video URL..."]')
                  const val = input?.value.trim()
                  if (val) {
                    setMediaUrl(val)
                    const type = detectMediaType(val)
                    setMediaFileType(type === "video" ? "video/mp4" : "image/png")
                  }
                }}
                className="px-3 py-2 rounded-lg bg-[#F37020]/20 text-[#F37020] text-xs font-medium hover:bg-[#F37020]/30 transition-colors"
              >
                Set
              </button>
            </div>
          )}

          {mediaUrl && (
            <div className="relative inline-block">
              {mediaFileType?.startsWith("video") ? (
                <div className="relative">
                  <video src={mediaUrl} className="max-h-32 rounded-lg border border-[#2A2A45]" muted playsInline preload="metadata" />
                  <Film className="absolute bottom-2 left-2 w-4 h-4 text-white/70" />
                </div>
              ) : (
                <img src={mediaUrl} alt="Preview" className="max-h-32 rounded-lg border border-[#2A2A45]" />
              )}
              <button
                onClick={removeMedia}
                className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
              {mediaFileName && <p className="text-[10px] text-white/30 mt-1 truncate max-w-[200px]">{mediaFileName}</p>}
            </div>
          )}
        </div>

        {/* CTA Builder */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/40 font-admin">CTA Buttons</span>
            {ctas.length < 3 && (
              <button
                onClick={addCta}
                className="px-2 py-1 rounded text-[10px] font-medium bg-[#0C0C12] text-white/40 border border-[#2A2A45] hover:border-white/10 transition-colors"
              >
                <Plus className="w-3 h-3 inline mr-0.5" />Add CTA
              </button>
            )}
            {ctas.length > 0 && (
              <span className="text-[10px] text-white/20">{ctas.length}/3</span>
            )}
          </div>
          {ctas.map((cta, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                value={cta.label}
                onChange={(e) => updateCta(i, "label", e.target.value)}
                maxLength={50}
                placeholder="Button label"
                className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
              <input
                type="text"
                value={cta.url}
                onChange={(e) => updateCta(i, "url", e.target.value)}
                maxLength={500}
                placeholder="https://... or /dashboard/deals"
                className="flex-[2] px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
              <button
                onClick={() => removeCta(i)}
                className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-3">
          <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
            <input
              type="radio"
              name="admin-target"
              checked={targetMode === "all"}
              onChange={() => setTargetMode("all")}
              className="accent-[#F37020]"
            />
            <Globe className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs font-medium text-white/60 font-admin">All Agencies</span>
          </label>
          <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
            <input
              type="radio"
              name="admin-target"
              checked={targetMode === "specific"}
              onChange={() => setTargetMode("specific")}
              className="accent-[#F37020]"
            />
            <Building2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-medium text-white/60 font-admin">Specific Agency</span>
          </label>
        </div>

        {targetMode === "specific" && (
          <select
            value={selectedAgencyId}
            onChange={(e) => setSelectedAgencyId(e.target.value)}
            className="mt-3 w-full px-4 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin outline-none focus:border-[#F37020]/40 transition-colors"
          >
            <option value="">Select an agency</option>
            {agencyTargets.map((t) => (
              <option key={t.agencyId} value={t.agencyId}>
                {t.agencyName} ({t.agencyId})
              </option>
            ))}
          </select>
        )}

        {feedback && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium ${
              feedback.type === "success"
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : "bg-red-500/10 text-red-400 border border-red-500/20"
            }`}
          >
            {feedback.text}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-white/20 font-mono-admin">{systemMessage.length}/2000</span>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              canSend
                ? "bg-[#F37020] text-white hover:bg-[#E06010]"
                : "bg-white/[0.04] text-white/20 cursor-not-allowed"
            }`}
          >
            {isPosting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Post Message
          </button>
        </div>
      </AdminCard>
    </div>
  )
}
